export enum Grade {
  A_PLUS = 'A+',
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  F = 'F',
}

export interface Subject {
  id: string;
  name: string;
  cat1: number;
  cat2: number;
  exam: number;
  score: number; // Total Achieved (Sum of CAT1 + CAT2 + Exam)
  total: number; // Max Possible (e.g. 100)
  teacherRemarks: string;
}

export interface HeaderSettings {
  isEnabled: boolean;
  layout: 'classic' | 'centered' | 'two-column';
  logo: {
    url: string | null;
    width: number;
    alignment: 'left' | 'center' | 'right';
    padding: number;
    customPositioning: boolean;
    x: number;
    y: number;
  };
  schoolName: {
    text: string;
    fontFamily: string;
    fontSize: number;
    color: string;
    bold: boolean;
    alignment: 'left' | 'center' | 'right';
  };
  address: {
    text: string;
    fontSize: number;
    alignment: 'left' | 'center' | 'right';
    lineHeight: number;
    color: string;
  };
  motto: {
    text: string;
    italic: boolean;
    bold: boolean;
    alignment: 'left' | 'center' | 'right';
    showDivider: boolean;
    color: string;
  };
  contact?: {
    phone: string;
    email: string;
  };
  spacing: {
    marginTop: number;
    marginBottom: number;
  };
}

export interface StudentData {
  name: string;
  studentId: string; // Used as School ID Number
  studentUid?: string; // For QR Code
  admissionNumber?: string;
  gradeLevel: string; // Class
  stream?: string;
  dob?: string;
  academicYear: string;
  programDuration?: number; // Total years (e.g. 4 for High School, 8 for Primary)
  admissionDate?: string; // Issued Date
  schoolName: string;
  schoolAddress?: string;
  schoolContact?: string;
  attendancePercentage: number;
  classTeacher: string;
  photoUrl?: string;
  subjects: Subject[];
  principalRemark: string;
  aiSummary?: string;
  headerSettings?: HeaderSettings;
}

export const INITIAL_SUBJECTS: Subject[] = [
  { id: '1', name: 'Mathematics', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '2', name: 'Science', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '3', name: 'English Language', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '4', name: 'History', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '5', name: 'Geography', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '6', name: 'Computer Science', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '7', name: 'Art & Design', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
  { id: '8', name: 'Physical Education', cat1: 0, cat2: 0, exam: 0, score: 0, total: 100, teacherRemarks: '' },
];

export const INITIAL_HEADER_SETTINGS: HeaderSettings = {
  isEnabled: false,
  layout: 'classic',
  logo: {
    url: null,
    width: 80,
    alignment: 'left',
    padding: 0,
    customPositioning: false,
    x: 40,
    y: 40
  },
  schoolName: {
    text: 'International Academy of Excellence',
    fontFamily: 'serif',
    fontSize: 28,
    color: '#312e81', // indigo-900
    bold: true,
    alignment: 'left'
  },
  address: {
    text: '123 Academic Avenue, Knowledge City, EduState 54321\nPhone: (555) 123-4567 | Email: contact@academy.edu',
    fontSize: 12,
    alignment: 'left',
    lineHeight: 1.5,
    color: '#64748b' // slate-500
  },
  motto: {
    text: 'Excellence in Education, Character in Life',
    italic: true,
    bold: false,
    alignment: 'left',
    showDivider: true,
    color: '#475569' // slate-600
  },
  spacing: {
    marginTop: 20,
    marginBottom: 20
  }
};

export const INITIAL_DATA: StudentData = {
  name: '',
  studentId: '',
  admissionNumber: '',
  gradeLevel: '',
  stream: '',
  dob: '',
  academicYear: new Date().getFullYear().toString(),
  schoolName: 'International Academy of Excellence',
  attendancePercentage: 100,
  classTeacher: '',
  photoUrl: '',
  subjects: INITIAL_SUBJECTS,
  principalRemark: '',
  aiSummary: '',
  headerSettings: INITIAL_HEADER_SETTINGS
};

export const SAMPLE_DATA: StudentData = {
  name: 'Alex Morgan',
  studentId: 'ID-882190',
  admissionNumber: 'ADM-2024-089',
  gradeLevel: '10th Grade',
  stream: 'Science A',
  dob: '2008-05-15',
  academicYear: '2024-2025',
  schoolName: 'International Academy of Excellence',
  attendancePercentage: 98,
  classTeacher: 'Mrs. Sarah Jenkins',
  photoUrl: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80',
  subjects: [
    { id: '1', name: 'Mathematics', cat1: 18, cat2: 19, exam: 58, score: 95, total: 100, teacherRemarks: 'Excellent analytical skills and problem solving.' },
    { id: '2', name: 'Science', cat1: 15, cat2: 18, exam: 55, score: 88, total: 100, teacherRemarks: 'Consistent effort shown in lab work.' },
    { id: '3', name: 'English Language', cat1: 19, cat2: 18, exam: 55, score: 92, total: 100, teacherRemarks: 'Outstanding essay writing abilities.' },
    { id: '4', name: 'History', cat1: 16, cat2: 14, exam: 54, score: 84, total: 100, teacherRemarks: 'Participates actively in class discussions.' },
    { id: '5', name: 'Geography', cat1: 14, cat2: 15, exam: 50, score: 79, total: 100, teacherRemarks: 'Needs to improve map reading interpretation.' },
    { id: '6', name: 'Computer Science', cat1: 20, cat2: 20, exam: 58, score: 98, total: 100, teacherRemarks: 'Exceptional programming projects submitted.' },
    { id: '7', name: 'Art & Design', cat1: 18, cat2: 19, exam: 54, score: 91, total: 100, teacherRemarks: 'Very creative and detailed portfolio.' },
    { id: '8', name: 'Physical Education', cat1: 19, cat2: 19, exam: 58, score: 96, total: 100, teacherRemarks: 'Demonstrates great leadership in team sports.' },
  ],
  principalRemark: 'Alex is a diligent student who sets a high standard for peers. The school is proud of these achievements. Keep up the fantastic work!',
  aiSummary: 'Alex has demonstrated outstanding academic performance this term, achieving an overall average that places them in the top percentile of the class.\n\nKey strengths are evident in Mathematics and Computer Science, where Alex shows a natural aptitude for logic and technical application. English and Art are also strong points, highlighting a well-rounded skill set.\n\nTo improve further, focusing on specific technical details in Geography would be beneficial. Overall, this is an exemplary report card that reflects hard work and dedication.',
  headerSettings: {
    ...INITIAL_HEADER_SETTINGS,
    isEnabled: true,
  }
};

export type ReportTemplate =
  | 'default'
  | 'minimal'
  | 'formal'
  | 'corporate'
  | 'creative'
  | 'tech'
  | 'elegant'
  | 'nature'
  | 'bold'
  | 'sidebar'
  | 'grid'
  | 'rose'
  | 'blossom'
  | 'royal'
  | 'slate'
  | 'mint'
  | 'oceanic'
  | 'crimson'
  | 'sunset'
  | 'glass'
  | 'classic-blue';

export const TEMPLATES: { id: ReportTemplate, name: string, description: string }[] = [
  { id: 'default', name: 'Default Indigo', description: 'Clean and professional.' },
  { id: 'rose', name: 'Rose Gold Elegant', description: 'Sophisticated pink and slate tones.' },
  { id: 'blossom', name: 'Cherry Blossom', description: 'Soft, airy pinks with rounded geometry.' },
  { id: 'royal', name: 'Royal Gold', description: 'Prestigious navy and gold academic style.' },
  { id: 'slate', name: 'Modern Slate', description: 'High contrast grey and black professional.' },
  { id: 'mint', name: 'Fresh Mint', description: 'Calming teal and mint greens.' },
  { id: 'oceanic', name: 'Deep Ocean', description: 'Professional blues and cyans.' },
  { id: 'crimson', name: 'Ivy League Crimson', description: 'Traditional academic red.' },
  { id: 'sunset', name: 'Warm Sunset', description: 'Gradient headers orange to rose.' },
  { id: 'glass', name: 'Glassmorphism', description: 'Trendy frosted glass effects.' },
  { id: 'classic-blue', name: 'Certificate Blue', description: 'Old-school certificate borders.' },
  { id: 'minimal', name: 'Modern Minimal', description: 'Clean, black and white, no distractions.' },
  { id: 'formal', name: 'Classic Formal', description: 'Traditional serif, academic borders.' },
  { id: 'corporate', name: 'Corporate Blue', description: 'Structured, navy headers, striped rows.' },
  { id: 'creative', name: 'Creative Spark', description: 'Playful colors and rounded geometry.' },
  { id: 'tech', name: 'Tech Matrix', description: 'Monospace font, digital aesthetics.' },
  { id: 'elegant', name: 'Warm Elegant', description: 'Sophisticated cream and gold palette.' },
  { id: 'nature', name: 'Eco Nature', description: 'Calming greens and soft corners.' },
  { id: 'bold', name: 'Bold Impact', description: 'High contrast, strong red accents.' },
  { id: 'sidebar', name: 'Sidebar Layout', description: 'Split layout with student info on the left.' },
  { id: 'grid', name: 'Grid Cards', description: 'Subjects displayed in a card grid.' },
];
